import { CardsSection } from "components/layout/Section/CardsSection";
import { Lock } from "lucide-react";
import { FC, ReactNode } from "react";
import { LP_TEXT } from "./legalProtectionTheme";

interface LegalProtectionSectionProps {
  /** Icon shown before the title, sized like the borrower sections (`h-8 w-8 text-primary`). */
  icon?: ReactNode;
  title: string;
  /** How many rows the section holds, shown after the title; hidden while there is none. */
  count?: number;
  /** Action on the right of the title. Not rendered while the section is disabled. */
  aside?: ReactNode;
  /** No company registered yet: the section is greyed out and carries no action. */
  disabled?: boolean;
  /** Replaces the action while the section is disabled, next to a padlock. */
  disabledHint?: string;
  /** One line under the title, for what the section expects (e.g. that it is optional). */
  hint?: string;
  children: ReactNode;
}

/**
 * Section of the legal protection first step: the shared `CardsSection`, as in the borrower first
 * step (icon, title, action on the right, greyed out while disabled). A disabled section shows a
 * padlocked hint in place of its action, since `CardsSection` greys out without blocking clicks.
 */
export const LegalProtectionSection: FC<LegalProtectionSectionProps> = ({
  icon,
  title,
  count,
  aside,
  disabled,
  disabledHint,
  hint,
  children
}) => (
  <CardsSection
    icon={icon}
    title={count ? `${title} (${count})` : title}
    disabled={disabled}
    titleAside={disabled
      ? disabledHint && (
        <span className={`inline-flex items-center gap-1.5 text-[13px] ${LP_TEXT.secondary}`}>
          <Lock className="h-4 w-4 flex-none" aria-hidden="true" />
          {disabledHint}
        </span>
      )
      : aside}
  >
    {/* The column gap already separates the hint from the cards; pulled up toward the title. */}
    {hint && <p className={`-mt-2 text-[14px] ${disabled ? LP_TEXT.disabled : LP_TEXT.secondary}`}>{hint}</p>}
    {children}
  </CardsSection>
);
